import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const BRAND_GREEN = '#0d1f0d';
const BRAND_GOLD  = '#c9a84b';

const NAV = [
  { to: '/',               label: 'Dashboard',      icon: '📊', roles: ['super_admin','admin','manager','viewer'] },
  { to: '/pipeline',       label: 'Pipeline',        icon: '🔄', roles: ['admin','manager','viewer'] },
  { to: '/revenue',        label: 'Revenue',         icon: '💰', roles: ['admin','manager','viewer'] },
  { to: '/jobs',           label: 'Jobs',            icon: '🔧', roles: ['admin','manager','viewer'] },
  { to: '/estimates',      label: 'Estimates',       icon: '📝', roles: ['admin','manager','viewer'] },
  { to: '/teams',          label: 'Teams',           icon: '👷', roles: ['admin','manager','viewer'] },
  { to: '/dispatch',       label: 'Dispatch Board',  icon: '🗓️', roles: ['admin','manager','viewer'] },
  { to: '/csr',            label: 'CSR Dashboard',   icon: '🎧', roles: ['admin','manager','viewer'] },
  { to: '/memberships',    label: 'Memberships',     icon: '🎫', roles: ['admin','manager','viewer'] },
  { to: '/forecast',       label: 'Budget/Forecast', icon: '📈', roles: ['admin','manager','viewer'] },
  { to: '/win-the-day',    label: 'Win the Day',     icon: '🏆', roles: ['admin','manager','viewer'] },
  { to: '/meetings',       label: 'L10 Meeting',     icon: '🧭', roles: ['admin','manager','viewer'] },
  { to: '/settings',       label: 'Settings',        icon: '⚙️',  roles: ['admin','manager'] },
  { to: '/admin/accounts', label: 'Accounts',        icon: '🏢', roles: ['admin'] },
  { to: '/admin/users',    label: 'Users',           icon: '👤', roles: ['admin'] },
  { to: '/superadmin',     label: 'Super Admin',     icon: '🌐', roles: ['super_admin'] },
];

function getAccountType(accountName) {
  const name = (accountName || '').toLowerCase();
  // The Custom Comfort brand is named just "Custom" - it only matched "custom comfort" before,
  // so it fell through to the Walker + Francis logos.
  if (name.includes('custom')) return 'customcomfort';
  if (name.includes('francis') && !name.includes('walker')) return 'francis';
  if (name.includes('walker') && !name.includes('francis')) return 'walker';
  if (name.includes('walker') && name.includes('francis')) return 'both';
  return null; // any other brand: no logo rather than another brand's
}

export default function Sidebar() {
  const { user, logout, switchAccount, myAccounts } = useAuth();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSwitch = async (e) => {
    const accountId = parseInt(e.target.value);
    if (!accountId || accountId === myAccounts?.currentAccountId) return;
    setSwitching(true);
    try {
      await switchAccount(accountId);
    } catch {
      setSwitching(false);
    }
  };

  const visibleNav = NAV.filter(n => n.roles.includes(user?.role));
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const showSwitcher = isAdmin || (myAccounts?.accounts?.length > 1);

  const currentAccount = myAccounts?.accounts?.find(a => a.id === myAccounts?.currentAccountId);
  const accountType = getAccountType(currentAccount?.name);

  // Sparticus & Friends Logic: if user has both 'climatecareclone' and 'walker home services'
  const hasClimate = myAccounts?.accounts?.some(a => a.name?.toLowerCase().includes('climatecareclone'));
  const hasWalkerHome = myAccounts?.accounts?.some(a => a.name?.toLowerCase().includes('walker home services'));
  const showSparticus = hasClimate && hasWalkerHome;
  const SPARTICUS_GROUP_ID = 999;
  const DEFAULT_ACCOUNT_ID = 1; // the rollup/"Default Account" (now "Rollup") - combined view
  const isRollup = myAccounts?.currentAccountId === SPARTICUS_GROUP_ID || myAccounts?.currentAccountId === DEFAULT_ACCOUNT_ID;

  return (
    <aside style={styles.sidebar}>
      <div style={styles.brand}>
        {isRollup || !accountType ? null : accountType === 'customcomfort' ? (
          <img src="/custom-comfort-logo.png" alt="Custom Comfort" style={styles.logo} />
        ) : accountType === 'both' ? (
          <div style={styles.bothLogos}>
            <img src="/walker-logo-color.png" alt="Walker" style={styles.logo} />
            <img src="/francis-logo-gold.png" alt="Francis" style={styles.logo} />
          </div>
        ) : (
          <img
            src={accountType === 'francis' ? '/francis-logo-gold.png' : '/walker-logo-color.png'}
            alt={accountType === 'francis' ? 'Francis' : 'Walker'}
            style={styles.logo}
          />
        )}
      </div>

      {showSwitcher && (
        <div style={styles.switcher}>
          <label style={styles.switcherLabel}>Business</label>
          {myAccounts?.accounts?.length > 1 ? (
            <select
              value={myAccounts.currentAccountId || ''}
              onChange={handleSwitch}
              disabled={switching}
              style={styles.switcherSelect}
            >
              {myAccounts.accounts.map(acc => (
                <option key={acc.id} value={acc.id}>{acc.name}</option>
              ))}
              {showSparticus && (
                <option key={SPARTICUS_GROUP_ID} value={SPARTICUS_GROUP_ID}>Sparticus & Friends</option>
              )}
            </select>
          ) : (
            <div style={{ ...styles.switcherSelect, cursor: 'default', color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', padding: '0.375rem 0.5rem' }}>
              {currentAccount?.name || 'Loading…'}
            </div>
          )}
          {switching && <div style={styles.switchingText}>Switching…</div>}
        </div>
      )}

      <nav style={styles.nav}>
        {visibleNav.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            style={({ isActive }) => ({
              ...styles.link,
              ...(isActive ? styles.linkActive : {}),
            })}
          >
            <span style={styles.linkIcon}>{item.icon}</span>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div style={styles.footer}>
        <div style={styles.userInfo}>
          <div style={styles.avatar}>{user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'}</div>
          <div>
            <p style={styles.userName}>{user?.name || user?.email}</p>
            <p style={styles.userRole}>{user?.role}</p>
          </div>
        </div>
        <button onClick={handleLogout} style={styles.logoutBtn} title="Sign out">↩</button>
      </div>
    </aside>
  );
}

const styles = {
  sidebar: {
    width: '220px',
    minHeight: '100vh',
    background: BRAND_GREEN,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    height: '100vh',
    overflow: 'auto',
    borderRight: `1px solid ${BRAND_GOLD}22`,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem 1rem 0.875rem',
    borderBottom: `1px solid ${BRAND_GOLD}22`,
  },
  logo: {
    width: '100%',
    maxWidth: '170px',
    height: 'auto',
    display: 'block',
    borderRadius: '0.3rem',
  },
  bothLogos: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
    alignItems: 'center',
    width: '100%',
  },
  switcher: {
    padding: '0.625rem 1rem',
    borderBottom: `1px solid ${BRAND_GOLD}22`,
  },
  switcherLabel: {
    display: 'block',
    fontSize: '0.65rem',
    fontWeight: '600',
    color: 'rgba(255,255,255,0.35)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: '0.3rem',
  },
  switcherSelect: {
    width: '100%',
    padding: '0.375rem 0.5rem',
    background: 'rgba(255,255,255,0.92)',
    border: `1px solid ${BRAND_GOLD}55`,
    borderRadius: '0.4rem',
    color: BRAND_GREEN,
    fontSize: '0.8rem',
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
  },
  switchingText: {
    fontSize: '0.7rem',
    color: 'rgba(255,255,255,0.35)',
    marginTop: '0.25rem',
    textAlign: 'center',
  },
  nav: { flex: 1, padding: '0.75rem 0.625rem', display: 'flex', flexDirection: 'column', gap: '0.125rem' },
  link: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.625rem',
    padding: '0.6rem 0.875rem',
    borderRadius: '0.5rem',
    color: 'rgba(255,255,255,0.75)',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'all 0.15s',
  },
  linkActive: {
    background: BRAND_GOLD,
    color: BRAND_GREEN,
    fontWeight: '700',
  },
  linkIcon: { fontSize: '1rem', width: '1.25rem', textAlign: 'center' },
  footer: {
    padding: '0.875rem',
    borderTop: `1px solid ${BRAND_GOLD}22`,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  userInfo: { display: 'flex', alignItems: 'center', gap: '0.625rem', flex: 1, overflow: 'hidden' },
  avatar: {
    width: '2rem',
    height: '2rem',
    borderRadius: '50%',
    background: BRAND_GOLD,
    color: BRAND_GREEN,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '0.875rem',
    flexShrink: 0,
  },
  userName: { color: '#fff', fontSize: '0.8rem', fontWeight: '600', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  userRole: { color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', margin: 0, textTransform: 'capitalize' },
  logoutBtn: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.4)',
    cursor: 'pointer',
    fontSize: '1.1rem',
    padding: '0.25rem',
    flexShrink: 0,
  },
};
